import * as angular from 'angular';

import { IRootScope } from '../../IRootScope';
import { IstateAuthorizationService, IUserSettingsService, IAuthorizationService, IConfigurationService, IStoreService } from '../../services';
import { toggleMinimizeMenu, treePinned, shellContentEnter } from '../../common';
import { ComponentDefinition, IApiInformation } from '../../models';
import { Store } from '../../stateManagement/Store';
import { StoreElement } from '../../stateManagement';

interface ISideBar {
    toggleMinimizeMenu: () => void;
}

class SideBarController implements angular.IComponentController, ISideBar {
    static controllerId = 'Sidebar';

    hoveredState: angular.ui.IState;
    isHovering: boolean;
    isLicenseValid: boolean;
    isMinimized: boolean;
    isTreePinned: boolean;
    loadApiInformation: () => angular.IPromise<IApiInformation>;
    states: angular.ui.IState[];

    readonly isMobileMode: boolean;

    private _hasHovered: boolean;
    private _timer;
    private _timeout: any;
    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$timeout',
        'storeService',
        'stateAuthorizationService',
        'userSettingsService',
        'authorizationService',
        'apiInformationActionsService',
        'configurationService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _stateAuthorizationService: IstateAuthorizationService,
        private _userSettingsService: IUserSettingsService,
        private _authorizationService: IAuthorizationService,
        private _configurationService: IConfigurationService
    ) {
    }

    $onInit() {
        this.isTreePinned = false;
        this._hasHovered = false;

        this._$scope.$on(treePinned, (event, data) => {
            this.isTreePinned = !!data;
            this._userSettingsService.setSettingValue('treePinned', String(this.isTreePinned));
        });

        this._$scope.$on(shellContentEnter, (event, data) => {
            this._hasHovered = false;
        });

        // restore state from cookies
        if (this._userSettingsService.getSettingValue('treePinned') === 'true') {
            this.mouseEnterState(this._$state.current);
            this._$rootScope.$broadcast(treePinned, true);
        }
        if (this._userSettingsService.getSettingValue('isMinimizedMenu') === 'true') {
            this.toggleMinimizeMenu();
        }

        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.ApiInformation, StoreElement.Authentication]
        );
        this._$scope.$on('$stateChangeSuccess', (event, toState) => this._$timeout(() => this.states = this.getStates()));
    }

    $onDestroy() {
        this.clearTimeout();
        this._unsubscribe();
    }

    private refreshStore(store: Store) {
        this.isLicenseValid = store.apiInformation.value?.isLicenseValid ?? false;
        this.states = this.getStates();
    }

    private clearTimeout() {
        if (this._timeout) {
            this._$timeout.cancel(this._timeout);
            this._timeout = undefined;
        }
    }

    mouseEnterState(state: angular.ui.IState) {
        this.isHovering = true;
        this.clearTimeout();
        this._timeout = this._$timeout(() => {
            if (this.isHovering) {
                if (this.isTreePinned && !this.hoveredState) {
                    this.hoveredState = this._$state.current;
                } else {
                    this.hoveredState = state;
                }
                this._hasHovered = true;
            }
        }, 200);
    }

    mouseLeaveState() {
        this.isHovering = false;
    }

    mouseLeaveSubMenu() {
        if (this._timer) {
            this._$timeout.cancel(this._timer);
        }
        this._hasHovered = false;
    }

    onLinkClick() {
        if (this.isMobileMode && this._hasHovered) {
            this.mouseLeaveSubMenu();
        }
    }

    onStateClick(state: angular.ui.IState) {
        if (this.isMobileMode) {
            if (this._hasHovered) {
                this._hasHovered = false;
            } else {
                this.mouseEnterState(state);
            }
        }
    }

    isActive(state: angular.ui.IState): boolean {
        const result = this._$state.includes(state.name)
            || (state.data.subMenu
                && (this._$state.includes(state.data.subMenu.subState) || this._$state.current.name.indexOf(state.data.subMenu.parentName) >= 0))
            || (state.name.indexOf('.') > 0 && this._$state.current.name.indexOf(state.name.split('.')[0]) === 0)
            || (state.data && state.data.subStates && state.data.subStates.indexOf(this._$state.current.name) >= 0);
        return result;
    }

    private getStates(): angular.ui.IState[] {
        if (this.isLicenseValid) {
            const states = this._$state.get()
                .filter(s => {
                    let isOk = !!s.data && !!s.data.visible && !!s.data.sortingOrder;
                    isOk = isOk && this._stateAuthorizationService.canAccess(s.name);
                    if (isOk && !!s.data && !!s.data.isExperimental) {
                        isOk = !!this._configurationService.configuration.useExperimentalFeatures;
                    }
                    if (isOk && this.isMobileMode) {
                        isOk = !!s.data.isMobileVisible;
                    }
                    return isOk;
                })
                .sort((a, b) => {
                    const result = a.data.sortingOrder - b.data.sortingOrder;
                    return result;
                });
            return states;
        } else {
            return [];
        }
    }

    toggleMinimizeMenu() {
        this.isMinimized = !this.isMinimized;
        this._userSettingsService.setSettingValue('isMinimizedMenu', String(this.isMinimized));
        this._$rootScope.$broadcast(toggleMinimizeMenu);
    }

    treeStyle() {
        if (this.isTreePinned) {
            return {
                'display': 'block'
            };
        }
    }

    isTreeVisible() {
        const val = this._authorizationService.isAuthenticated() && this._authorizationService.isConnected() &&
            (this.isTreePinned || (this._hasHovered && this.hoveredState.data.subMenu)) && this._$state.current && this._$state.current.name !== 'workspace';
        return val;
    }
}

export const wtSideBar: ComponentDefinition = {
    componentId: 'wtSideBar',
    controller: SideBarController,
    template: require('./sideBar.component.html'),
    controllerAs: 'vm',
    bindings: {
        isMobileMode: '<?'
    }
};